import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { CompanyRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddCompany = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CompanyRequest) => api.postCompaniesV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.companies.new.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.companies.new.notification.failed"), variant: "alert" });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMPANIES(projectId) });
    },
  });
};

const useEditCompany = (companyId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CompanyRequest) => api.putCompaniesV1(companyId, data),
    onSuccess() {
      showFlashToast({ message: t("page.companies.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.companies.edit.notification.failed"), variant: "alert" });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMPANIES(projectId) });
    },
  });
};

const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (companyId: string) => api.deleteCompaniesByIdV1(companyId),
    onSuccess() {
      showFlashToast({ variant: "success", message: t("page.companies.actions.delete.notifications.success") });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.companies.actions.delete.notifications.failed") });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMPANIES(projectId) });
    },
  });
};

const useRefreshLocationCode = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (companyId: string) => api.putCompaniesRegenerateLocationCodeV1(companyId),
    onSuccess() {
      showFlashToast({
        variant: "success",
        message: t("page.companies.actions.refresh-location-code.notifications.success"),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.companies.actions.refresh-location-code.notifications.failed"),
      });
    },
    onSettled(_data, _error, companyId) {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMPANIES(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMPANY_DETAILS(projectId, companyId) });
    },
  });
};

const useBulkUploadCompany = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: CompanyRequest) => api.postCompaniesV1(data),
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMPANIES(projectId) });
    },
  });
};

export const companyMutations = {
  useAddCompany,
  useEditCompany,
  useDeleteCompany,
  useRefreshLocationCode,
  useBulkUploadCompany,
};
